import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Row, Col, Card, CardBody, CardHeader, Input, Label, FormGroup, Button, Badge
} from "reactstrap";
import { OverlayLoader } from "../../../../components/Common";
import { PERMISSION_GROUPS } from "../constants";
import { roleDefaults } from "../../../../models/role";
import { GLOBAL_STATUS } from "../../../../constants/common";
import { GLOBAL_STATUS_LABELS } from "../../../../constants/common";;

/**
 * PermissionEditor
 * Editor que coloca tudo em um único Card, sem blocos flutuantes, compacto e direto.
 */
const PermissionEditor = ({ role, onSave, onDelete, isSaving, onChange }) => {
    const [formData, setFormData] = useState(roleDefaults);

    useEffect(() => {
        if (role) {
            setFormData({
                id: role.id || null,
                name: role.name || "",
                description: role.description || "",
                status: role.status || GLOBAL_STATUS.ACTIVE,
                permissions: role.permissions || {}
            });
        } else {
            setFormData(roleDefaults);
        }
    }, [role]);

    const handleInputChange = (e) => {
        const { id, value, checked } = e.target;
        if (onChange) onChange(true);
        if (id === "statusSwitch") {
            setFormData(prev => ({ ...prev, status: checked ? GLOBAL_STATUS.ACTIVE : GLOBAL_STATUS.INACTIVE }));
        } else {
            setFormData(prev => ({ ...prev, [id]: value }));
        }
    };

    const togglePermission = (permissionId) => {
        if (onChange) onChange(true);
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [permissionId]: !prev.permissions[permissionId]
            }
        }));
    };

    const toggleGroup = (groupPermissions, isAllActive) => {
        if (onChange) onChange(true);
        const newPermissions = { ...formData.permissions };
        groupPermissions.forEach(perm => {
            newPermissions[perm.id] = !isAllActive;
        });
        setFormData(prev => ({ ...prev, permissions: newPermissions }));
    };

    const handleDuplicate = () => {
        setFormData(prev => ({
            ...prev,
            id: null,
            name: `${prev.name} (Cópia)`
        }));
        if (onChange) onChange(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(formData);
    };

    if (!role && !formData.name) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center animate-fadeIn" style={{ minHeight: "60vh" }}>
                <div className="avatar-xl mb-4">
                    <span className="avatar-title bg-soft-primary text-primary rounded-circle shadow-sm" style={{ fontSize: "48px" }}>
                        <i className="mdi mdi-shield-account-outline"></i>
                    </span>
                </div>
                <h4 className="fw-semibold text-dark">Gerenciamento de Cargos</h4>
                <p className="text-muted w-50">Selecione um cargo na lista lateral para visualizar suas permissões ou clique em <strong>"Novo"</strong> para criar um cargo.</p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="fade-in pb-4 h-100">
            <Card className="border-0 shadow-sm h-100 overflow-hidden position-relative rounded-0">
                {/* CABEÇALHO DO CARD (Sticky) */}
                <CardHeader className="bg-white border-bottom py-3 sticky-top" style={{ zIndex: 10 }}>
                    <div className="d-flex align-items-center">
                        <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                            <div className="avatar-sm flex-shrink-0">
                                <span className="avatar-title bg-primary text-white rounded bg-gradient shadow-sm">
                                    <i className="mdi mdi-shield-check-outline font-size-22"></i>
                                </span>
                            </div>
                            <div className="ms-3 overflow-hidden">
                                <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                    <span className="text-truncate">{formData.id ? formData.name : formData.name || "Novo Cargo"}</span>
                                    {formData.id && (
                                        <Badge color={formData.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                            {GLOBAL_STATUS_LABELS[formData.status] || 'Desconhecido'}
                                        </Badge>
                                    )}
                                </h5>
                                <p className="text-muted mb-0 small text-truncate">Gerenciar permissões do perfil</p>
                            </div>
                        </div>

                        <div className="d-flex gap-2 flex-shrink-0 ms-3">
                            {formData.id && (
                                <div className="d-none d-lg-flex gap-2">
                                    <Button
                                        type="button"
                                        color="light"
                                        className="btn-soft-secondary waves-effect shadow-none border-0"
                                        onClick={handleDuplicate}
                                        title="Duplicar"
                                    >
                                        <i className="mdi mdi-content-copy"></i>
                                    </Button>
                                    <Button
                                        type="button"
                                        color="danger"
                                        outline
                                        className="btn-soft-danger waves-effect shadow-none border-0"
                                        onClick={() => onDelete(formData.id)}
                                        title="Excluir"
                                    >
                                        <i className="mdi mdi-trash-can-outline"></i>
                                    </Button>
                                </div>
                            )}
                            <Button
                                type="submit"
                                color="primary"
                                className="waves-effect waves-light bg-gradient shadow-sm px-4"
                                disabled={isSaving || !formData.name}
                            >
                                {isSaving ? "Salvando" : "Salvar"}
                            </Button>
                        </div>
                    </div>
                </CardHeader>

                <CardBody className="p-4">
                    <OverlayLoader show={isSaving} label="Salvando cargo..." />
                    {/* INFORMAÇÕES BÁSICAS */}
                    <div className="mb-4 pb-4 border-bottom">
                        <div className="d-flex align-items-center mb-3">
                            <h6 className="mb-0 fw-semibold text-dark flex-grow-1">Informações do Perfil</h6>
                            <div className="form-check form-switch mb-0" dir="ltr">
                                <Input
                                    type="switch"
                                    id="statusSwitch"
                                    className="form-check-input mt-1 cursor-pointer"
                                    checked={formData.status === GLOBAL_STATUS.ACTIVE}
                                    onChange={handleInputChange}
                                />
                                <Label className="form-check-label mb-0 fw-medium cursor-pointer small" htmlFor="statusSwitch">
                                    {GLOBAL_STATUS_LABELS[formData.status] || formData.status}
                                </Label>
                            </div>
                        </div>

                        <Row>
                            <Col lg={4} md={6}>
                                <FormGroup className="mb-3 mb-md-0">
                                    <Label htmlFor="name" className="fw-medium text-muted small text-uppercase">Nome do Cargo <span className="text-danger">*</span></Label>
                                    <Input
                                        id="name"
                                        placeholder="Ex: Treinador, Recepcionista"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        autoFocus
                                        className="form-control"
                                        style={{ fontSize: '14px' }}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col lg={8} md={6}>
                                <FormGroup className="mb-0">
                                    <Label htmlFor="description" className="fw-medium text-muted small text-uppercase">Breve Descrição</Label>
                                    <Input
                                        id="description"
                                        placeholder="Qual a principal função deste papel no sistema?"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        style={{ fontSize: '14px' }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>

                    {/* MATRIZ DE PERMISSÕES (Sem afastamento, layout contínuo) */}
                    <h6 className="mb-3 fw-semibold text-dark">Matriz de Permissões</h6>
                    <div className="rounded-0 border overflow-hidden">
                        {PERMISSION_GROUPS.map((group, gIdx) => {
                            const activeCount = group.permissions.filter(p => formData.permissions[p.id]).length;
                            const totalCount = group.permissions.length;
                            const isAllActive = activeCount === totalCount && totalCount > 0;
                            const isLast = gIdx === PERMISSION_GROUPS.length - 1;

                            return (
                                <div key={gIdx} className={!isLast ? "border-bottom" : ""}>
                                    {/* Cabeçalho do Grupo (Linha contínua) */}
                                    <div className="bg-light bg-opacity-75 py-2 px-3 d-flex justify-content-between align-items-center border-bottom">
                                        <h6 className="mb-0 fw-semibold text-dark d-flex align-items-center font-size-13">
                                            <i className="mdi mdi-shield-key-outline text-primary me-2 font-size-16"></i>
                                            {group.name}
                                        </h6>
                                        <button
                                            type="button"
                                            className={`btn btn-sm shadow-none p-0 fw-medium ${isAllActive ? 'text-primary' : 'text-muted'}`}
                                            onClick={() => toggleGroup(group.permissions, isAllActive)}
                                            style={{ fontSize: "12px" }}
                                        >
                                            {isAllActive ?
                                                <><i className="mdi mdi-check-all me-1"></i> Desmarcar Todos</> :
                                                <><i className="mdi mdi-check-underline me-1"></i> Marcar Todos</>}
                                        </button>
                                    </div>

                                    {/* Grid de Permissões */}
                                    <div className="p-3 bg-white">
                                        <Row>
                                            {group.permissions.map((perm) => (
                                                <Col md={6} lg={4} key={perm.id}>
                                                    <div className="form-check form-switch py-2 ps-5 pe-3 rounded hover-bg-light transition-all mb-0" style={{ cursor: 'pointer', minHeight: '38px' }}>
                                                        <Input
                                                            type="switch"
                                                            id={`perm-${perm.id}`}
                                                            checked={formData.permissions[perm.id] === true}
                                                            onChange={() => togglePermission(perm.id)}
                                                            className="form-check-input mt-2 cursor-pointer"
                                                        />
                                                        <Label
                                                            className="form-check-label small mb-0 ms-1 cursor-pointer text-truncate d-block pt-1"
                                                            htmlFor={`perm-${perm.id}`}
                                                            title={perm.label}
                                                            style={{ marginTop: '1px' }}
                                                        >
                                                            {perm.label}
                                                        </Label>
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardBody>
            </Card>
        </form>
    );
};

PermissionEditor.propTypes = {
    role: PropTypes.object,
    onSave: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
    onChange: PropTypes.func
};

export default PermissionEditor;
